import React, { useState } from "react";
import { View, StyleSheet, TouchableOpacity } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { IconSymbol } from "@/components/ui/IconSymbol";
import { useTheme } from "@/hooks/useTheme";
import { Colors } from "@/constants/Colors";
import { useColorScheme } from "@/hooks/useColorScheme";

type QuickSettingsSectionProps = {
  title: string;
  children: React.ReactNode;
  hasOpenDropdown?: boolean;
};

/**
 * A collapsible section for quick settings with proper z-index handling.
 * When a dropdown inside is open, the section elevates to prevent
 * the dropdown menu from painting behind sibling sections.
 */
export function QuickSettingsSection({ title, children, hasOpenDropdown }: QuickSettingsSectionProps) {
  const { colors } = useTheme();
  const theme = useColorScheme() ?? "light";
  const [isOpen, setIsOpen] = useState(false);

  return (
    <View style={[styles.container, { borderColor: colors.border }, hasOpenDropdown && styles.elevated]}>
      <TouchableOpacity style={styles.heading} onPress={() => setIsOpen((value) => !value)} activeOpacity={0.8}>
        <IconSymbol
          name="chevron-right"
          size={18}
          color={theme === "light" ? Colors.light.icon : Colors.dark.icon}
          style={{ transform: [{ rotate: isOpen ? "90deg" : "0deg" }] }}
        />
        <ThemedText type="defaultSemiBold">{title}</ThemedText>
      </TouchableOpacity>
      {isOpen && <View style={styles.content}>{children}</View>}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    borderTopWidth: StyleSheet.hairlineWidth,
    paddingTop: 12,
    gap: 8,
  },
  elevated: {
    zIndex: 50,
    elevation: 50,
  },
  heading: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 4,
    gap: 6,
  },
  content: {
    marginTop: 6,
    marginLeft: 24,
    gap: 8,
  },
});
